import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { naira, compactNaira } from "../../lib/format";
import { HC } from "./theme";
import { Card, SectionHead, Btn, Table, Field, Input, Select, Pill } from "./ui";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Users, CalendarCheck, Banknote, Plane, Clock, UserMinus } from "lucide-react";
import { downloadReport } from "../accounting/exportHelpers";

const REPORTS = [
  { key: "headcount", label: "Headcount", Icon: Users, sub: "Employees by department, position and location" },
  { key: "attendance", label: "Attendance", Icon: CalendarCheck, sub: "Presence, lateness and overtime per day" },
  { key: "payroll-cost", label: "Payroll Cost", Icon: Banknote, sub: "Gross, deductions and net by location/department" },
  { key: "leave", label: "Leave", Icon: Plane, sub: "Requests, approvals and days taken" },
  { key: "overtime", label: "Overtime", Icon: Clock, sub: "Hours and cost per employee" },
];

const COLS = {
  headcount: [
    { key: "employee_no", label: "EMP NO" }, { key: "name", label: "NAME" },
    { key: "department", label: "DEPARTMENT" }, { key: "position", label: "POSITION" },
    { key: "location", label: "LOCATION" }, { key: "status", label: "STATUS", render: (r) => <Pill status={r.status} /> },
    { key: "gross_monthly", label: "GROSS", align: "right", render: (r) => <span className="tabular-nums">{naira(r.gross_monthly)}</span> },
  ],
  attendance: [
    { key: "date", label: "DATE" }, { key: "employee_name", label: "EMPLOYEE" },
    { key: "department", label: "DEPARTMENT" }, { key: "location", label: "LOCATION" },
    { key: "status", label: "STATUS", render: (r) => <Pill status={r.status} /> },
    { key: "clock_in", label: "IN" }, { key: "clock_out", label: "OUT" },
    { key: "overtime_minutes", label: "OT MIN", align: "right" },
  ],
  "payroll-cost": [
    { key: "name", label: "GROUP" }, { key: "employees", label: "EMPLOYEES", align: "right" },
    { key: "gross", label: "GROSS", align: "right", render: (r) => <span className="tabular-nums">{naira(r.gross)}</span> },
    { key: "deductions", label: "DEDUCTIONS", align: "right", render: (r) => <span className="tabular-nums">{naira(r.deductions)}</span> },
    { key: "net", label: "NET", align: "right", render: (r) => <span className="tabular-nums">{naira(r.net)}</span> },
    { key: "overtime", label: "OVERTIME", align: "right", render: (r) => <span className="tabular-nums">{naira(r.overtime)}</span> },
  ],
  leave: [
    { key: "employee_name", label: "EMPLOYEE" }, { key: "leave_type", label: "TYPE" },
    { key: "date_from", label: "FROM" }, { key: "date_to", label: "TO" },
    { key: "days", label: "DAYS", align: "right" },
    { key: "status", label: "STATUS", render: (r) => <Pill status={r.status} /> },
  ],
  overtime: [
    { key: "employee_name", label: "EMPLOYEE" }, { key: "department", label: "DEPARTMENT" },
    { key: "location", label: "LOCATION" }, { key: "hours", label: "HOURS", align: "right" },
    { key: "cost", label: "COST", align: "right", render: (r) => <span className="tabular-nums">{naira(r.cost)}</span> },
  ],
};

export default function HRReports() {
  const { locationId } = useOutletContext();
  const [active, setActive] = useState("headcount");
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [groupBy, setGroupBy] = useState("location");
  const [turnover, setTurnover] = useState(null);

  const paramsFor = () => ({
    location_id: locationId || undefined,
    period: ["payroll-cost", "overtime"].includes(active) ? period : undefined,
    date_from: active === "attendance" ? from || undefined : undefined,
    date_to: active === "attendance" ? to || undefined : undefined,
    group_by: active === "payroll-cost" ? groupBy : undefined,
  });

  const load = () => api.get(`/hr/reports/${active}`, { params: paramsFor() })
    .then((d) => setRows(Array.isArray(d) ? d : []))
    .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, period, from, to, groupBy, locationId]);
  useEffect(() => { api.get("/hr/reports/turnover").then(setTurnover).catch(() => {}); }, []);

  return (
    <div className="space-y-4" data-testid="hr-reports-page">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        {REPORTS.map((r) => (
          <button key={r.key} data-testid={`hr-report-${r.key}`} onClick={() => setActive(r.key)}
            className="text-left rounded-2xl border p-4 transition-colors"
            style={{ borderColor: active === r.key ? HC.burgundy : HC.border, background: active === r.key ? HC.burgundySoft : "#fff" }}>
            <r.Icon size={18} style={{ color: active === r.key ? HC.burgundy : HC.gold }} />
            <div className="font-brand font-bold text-[14px] mt-2" style={{ color: HC.text }}>{r.label}</div>
            <div className="text-[11.5px] mt-0.5" style={{ color: HC.soft }}>{r.sub}</div>
          </button>
        ))}
      </div>

      <Card>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          {["payroll-cost", "overtime"].includes(active) && (
            <Field label="Period" className="lg:w-44"><Input data-testid="rep-period" value={period} onChange={(e) => setPeriod(e.target.value)} /></Field>
          )}
          {active === "payroll-cost" && (
            <Field label="Group by" className="lg:w-44">
              <Select data-testid="rep-groupby" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                <option value="location">Location</option><option value="department">Department</option>
              </Select>
            </Field>
          )}
          {active === "attendance" && (
            <>
              <Field label="From" className="lg:w-44"><Input type="date" data-testid="rep-from" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
              <Field label="To" className="lg:w-44"><Input type="date" data-testid="rep-to" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
            </>
          )}
          <div className="flex-1" />
          <Btn variant="gold" data-testid="rep-csv"
            onClick={() => downloadReport(`/hr/reports/exports/${active}.csv`, `hr-${active}.csv`, paramsFor())}>
            <FileSpreadsheet size={15} /> CSV
          </Btn>
          <Btn data-testid="rep-pdf"
            onClick={() => downloadReport(`/hr/reports/exports/${active}.pdf`, `hr-${active}.pdf`, paramsFor())}>
            <FileText size={15} /> PDF
          </Btn>
        </div>
      </Card>

      <Card>
        <SectionHead title={`${REPORTS.find((r) => r.key === active)?.label} report`} sub={`${rows.length} row(s)`} />
        <Table testid="hr-report-table" rows={rows.slice(0, 300)} columns={COLS[active]} empty="No data for this filter" rowKey={(r, i) => i} />
      </Card>

      {turnover && (
        <Card testid="hr-turnover">
          <SectionHead title="Employee turnover" sub="Rolling 12 months" />
          <div className="flex flex-wrap gap-3">
            {[["Active employees", turnover.active], ["Exits", turnover.exits], ["Turnover rate", `${turnover.turnover_pct}%`]].map(([k, v]) => (
              <div key={k} className="rounded-xl border px-4 py-3" style={{ borderColor: HC.border }}>
                <div className="text-[10px] font-bold tracking-[0.14em]" style={{ color: HC.soft }}>{k.toUpperCase()}</div>
                <div className="font-brand font-bold text-xl" style={{ color: HC.text }}>{v}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
